import { z } from 'zod';
import { CuidSchema, ListQuerySchema } from './crm.schemas.js';
import { PACKAGE_LAYOUTS, PACKAGE_PRICING_MODES } from './packages.js';

/**
 * مخططات باقات الخدمات.
 */

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const money = z
  .number()
  .min(0, 'القيمة لا يمكن أن تكون سالبة.')
  .max(99_999_999_999.99, 'القيمة أكبر من الحد المسموح.');

export const PackageItemSchema = z.object({
  serviceId: CuidSchema,
  quantity: z.number().positive('الكمية يجب أن تكون أكبر من صفر.').max(999_999).default(1),
  /** فارغ يعني اعتماد سعر الخدمة المفرد. */
  unitPrice: money.nullable().optional(),
  description: optionalText(300),
});
export type PackageItemInput = z.infer<typeof PackageItemSchema>;

/**
 * التحقق من اتساق التسعير.
 *
 * حفظ باقة بسعر مقطوع دون قيمة، أو بخصم دون نسبة، ينتج باقة تُسعَّر بصفر
 * دون أن ينتبه أحد حتى تصل إلى عرض سعر عند عميل.
 */
const withPricingRules = <T extends z.ZodTypeAny>(schema: T) =>
  schema.superRefine((v: any, ctx: z.RefinementCtx) => {
    if (v.pricingMode === 'FIXED' && (v.fixedPrice === null || v.fixedPrice === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fixedPrice'],
        message: 'السعر المقطوع مطلوب مع هذه الطريقة.',
      });
    }
    if (
      v.pricingMode === 'DISCOUNT' &&
      (v.discountPercent === null || v.discountPercent === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountPercent'],
        message: 'نسبة الخصم مطلوبة مع هذه الطريقة.',
      });
    }
  });

const PackageBase = z.object({
  code: optionalText(40),
  name: z.string().trim().min(2, 'اسم الباقة مطلوب.').max(160),
  description: optionalText(1000),
  category: optionalText(80),
  /* غلافُ الباقة — مضمَّنٌ كغلاف الخدمة. */
  imageUrl: z.string().trim().max(700_000).nullable().optional(),

  pricingMode: z.enum(PACKAGE_PRICING_MODES).default('SUM'),
  fixedPrice: money.nullable().optional(),
  discountPercent: z
    .number()
    .min(0, 'النسبة لا يمكن أن تكون سالبة.')
    .max(100, 'النسبة لا تتجاوز مائة بالمائة.')
    .nullable()
    .optional(),

  layout: z.enum(PACKAGE_LAYOUTS).default('DETAILED'),

  /*
   * الحالةُ تُضبط منذ الإنشاء.
   *
   * باقةٌ تُبنى على مهلٍ وتُترك موقوفةً حتى تكتمل — وبلا هذا كان لا بدّ
   * من حفظها متاحةً ثمّ فتحها ثانيةً لإيقافها، وبينهما تظهر في العروض
   * ناقصة.
   */
  isActive: z.boolean().default(true),
  durationDays: z.number().int().positive().max(3650).nullable().optional(),

  /**
   * الباقة بلا خدمات لا معنى لها: سعرها صفر ووصفها فارغ، وإدراجها في عرض
   * ينتج بندًا خاويًا.
   */
  items: z.array(PackageItemSchema).min(1, 'الباقة تحتاج خدمة واحدة على الأقل.').max(60),
});

export const CreatePackageSchema = withPricingRules(PackageBase);
export type CreatePackageInput = z.infer<typeof PackageBase>;

export const UpdatePackageSchema = withPricingRules(PackageBase.partial());
export type UpdatePackageInput = z.infer<typeof PackageBase> extends infer T
  ? Partial<T> & { isActive?: boolean }
  : never;

export const PackageListQuerySchema = ListQuerySchema.extend({
  category: z.string().trim().max(80).optional(),
  /* لا coerce هنا: النصّ "false" كان سيصير صحيحًا فيقلب المرشّح. */
  isActive: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});
export type PackageListQuery = z.infer<typeof PackageListQuerySchema>;

/** معاينة الباقة كبنود مستند قبل إدراجها فعليًا. */
export const ExpandPackageSchema = z.object({
  layout: z.enum(PACKAGE_LAYOUTS).optional(),
  quantity: z.number().int().positive().max(999).default(1),
});
export type ExpandPackageInput = z.infer<typeof ExpandPackageSchema>;
